import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { fileRequestApi, editorApi, verticalsApi, regenApi } from '../lib/api';
import { formatDate, formatDateTime, canUseStudio } from '../lib/utils';
import { Inbox, Plus, Link as LinkIcon, Copy, XCircle, Trash2, CheckCircle, UserPlus, Search, Filter, List, Grid, Rocket, RotateCcw, Eye, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { CreateFileRequestModal } from '../components/CreateFileRequestModal';
import { DeadlineChip } from '../components/DeadlineChip';
import { FileRequestDetailsModal } from '../components/FileRequestDetailsModal';
import { OrbitModal } from '../components/OrbitModal';
import { ReassignFileRequestModal } from '../components/ReassignFileRequestModal';
import { useAuth, isAdminRole } from '../contexts/AuthContext';
import { VERTICALS as STATIC_VERTICALS } from '../constants/verticals';
import { PLATFORMS } from '../constants/platforms';
import { getVerticalBadgeClasses, getStatusBadgeClasses } from '../constants/statusColors';

interface FileRequest {
  id: string;
  title: string;
  description?: string;
  request_token: string;
  is_active: boolean;
  status?: 'open' | 'in_progress' | 'uploaded' | 'launched' | 'closed' | 'reopened';
  deadline?: string;
  folder_name?: string;
  upload_count: number;
  created_at: string;
  closed_at?: string;
  request_type?: string;
  platform?: string;
  vertical?: string;
  concept_notes?: string;
  num_creatives?: number;
  buyer_name?: string;
  buyer_email?: string;
  created_by?: string; // User ID of creator
  created_by_name?: string;
  assigned_buyer_id?: string; // User ID of assigned buyer
  assigned_editors?: string; // STRING_AGG returns a comma-separated string
  completed_editors_count?: number;
  total_editors_count?: number;
  in_progress_editors_count?: number;
  pending_editors_count?: number;
  uploaded_files_count?: number;
  my_uploaded_files_count?: number;
  my_creatives_assigned?: number;
  my_creatives_completed?: number;
  my_deadline_at?: string | null;
  my_delivered_at?: string | null;
  my_deadline_missed?: boolean;
  // 🆕 Multi-platform/vertical support
  platforms?: string[];
  verticals?: string[];
  canvas_headline?: string;
  tested_files_count?: number;
  untested_files_count?: number;
  scheduled_files_count?: number;
  launched_files_count?: number;
  not_launched_files_count?: number;
}

export function FileRequestsPage() {
  const { user, authReady } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<FileRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'closed'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [duplicateData, setDuplicateData] = useState<any>(null);
  const [selectedRequest, setSelectedRequest] = useState<FileRequest | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [requestToReassign, setRequestToReassign] = useState<FileRequest | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVertical, setSelectedVertical] = useState<string>('All');
  const [selectedPlatform, setSelectedPlatform] = useState<string>('All');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedEditorIds, setSelectedEditorIds] = useState<string[]>([]);
  const [selectedMediaType, setSelectedMediaType] = useState<'all' | 'image' | 'video'>('all');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<string>('desc');
  const [editors, setEditors] = useState<any[]>([]);
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRequests, setTotalRequests] = useState(0);
  const PAGE_SIZE = 50;
  // Dynamic verticals (fetched from API, falls back to static constants)
  const [verticals, setVerticals] = useState<string[]>([...STATIC_VERTICALS]);

  // Multi-select
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  // Debounce timer for search
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchVerticals = useCallback(() => {
    verticalsApi.getAll().then(res => {
      const data: string[] = res.data?.data;
      if (Array.isArray(data) && data.length > 0) setVerticals(data);
    }).catch(() => { /* keep static fallback */ });
  }, []);

  useEffect(() => {
    fetchEditors();
    fetchVerticals();
    window.addEventListener('orbit:verticals-updated', fetchVerticals);
    return () => window.removeEventListener('orbit:verticals-updated', fetchVerticals);
  }, [fetchVerticals]);

  const fetchEditors = async () => {
    try {
      const response = await editorApi.getAll();
      setEditors(response.data.data || []);
    } catch (error: any) {
      console.error('Failed to fetch editors:', error);
    }
  };

  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const params: any = {
        status: filter,
        page: currentPage,
        limit: PAGE_SIZE,
      };

      // Server-side filters
      if (searchQuery) params.search = searchQuery;
      if (selectedVertical !== 'All') params.vertical = selectedVertical;
      if (selectedPlatform !== 'All') params.platform = selectedPlatform;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (selectedEditorIds.length > 0) {
        params.editor_ids = selectedEditorIds.join(',');
      }
      if (selectedMediaType !== 'all') {
        params.media_type = selectedMediaType;
      }
      if (sortBy) params.sort_by = sortBy;
      if (sortOrder) params.sort_order = sortOrder;

      const response = await fileRequestApi.getAll(params);
      const data = response?.data?.data;
      if (Array.isArray(data)) {
        setRequests(data);
      } else {
        // Defensive: log unexpected shape so we can diagnose silent breakage
        console.warn('file-requests response had unexpected shape', response?.data);
        setRequests([]);
      }
      if (response.data.pagination) {
        setTotalPages(response.data.pagination.totalPages || 1);
        setTotalRequests(response.data.pagination.total || 0);
      }
    } catch (error: any) {
      console.error('Failed to fetch file requests:', error);
      // Surface the failure to the user instead of silently showing "No file requests found".
      // 401 is handled by the api interceptor (soft logout); for everything else, show a banner.
      if (error?.response?.status !== 401) {
        const msg = error?.response?.data?.error
          || (error?.response?.status >= 500 ? 'Server error — please refresh in a moment.'
              : error?.response?.status === undefined ? 'Network error — check your connection and refresh.'
              : `Failed to load file requests (HTTP ${error?.response?.status})`);
        setFetchError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [filter, currentPage, searchQuery, selectedVertical, selectedPlatform, dateFrom, dateTo, selectedEditorIds, selectedMediaType, sortBy, sortOrder]);

  // Reset to page 1 and clear selections when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds(new Set());
  }, [filter, dateFrom, dateTo, selectedEditorIds, selectedMediaType, selectedVertical, selectedPlatform]);

  useEffect(() => {
    if (!authReady) return; // wait for AuthContext to settle to avoid stale-role fetches
    fetchRequests();
    setSelectedIds(new Set());
  }, [fetchRequests, authReady]);

  // Auto-open request details when coming from a notification
  useEffect(() => {
    const openId = searchParams.get('openRequestId');
    if (openId) {
      setSelectedRequest({ id: openId } as any);
      setShowDetailsModal(true);
      // Clear param so it doesn't re-open on refresh
      searchParams.delete('openRequestId');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search - triggers fetch after user stops typing
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setCurrentPage(1);
    }, 400);
  };

  const getPublicUrl = (token: string) => {
    return `${window.location.origin}/request/${token}`;
  };

  const handleCopyLink = (token: string) => {
    const url = getPublicUrl(token);
    navigator.clipboard.writeText(url);
    alert('Link copied to clipboard!');
  };

  // ✨ Generate with ORBIT (admin-only): a real modal that shows the WORK ORDER it parsed out of
  // the request (which script, which source clip, how many) so a misread is caught before we
  // spend. On start we drop the user straight into the Creative Team Room to watch it build.
  const [orbitFor, setOrbitFor] = useState<any>(null);

  const handleCloseRequest = async (id: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to close this request? It will no longer accept uploads.')) {
      return;
    }

    try {
      await fileRequestApi.close(id);
      fetchRequests();
    } catch (error: any) {
      console.error('Failed to close request:', error);
      alert(error.response?.data?.error || 'Failed to close request');
    }
  };

  const handleDeleteRequest = async (id: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to delete this request? This action cannot be undone.')) {
      return;
    }

    try {
      await fileRequestApi.delete(id);
      fetchRequests();
    } catch (error: any) {
      console.error('Failed to delete request:', error);
      alert(error.response?.data?.error || 'Failed to delete request');
    }
  };

  const handleViewDetails = (request: FileRequest) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  const handleReassign = (request: FileRequest) => {
    setRequestToReassign(request);
    setShowReassignModal(true);
  };

  const handleLaunchRequest = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Mark this request as launched?')) {
      return;
    }

    try {
      await fileRequestApi.launch(id);
      fetchRequests();
    } catch (error: any) {
      console.error('Failed to launch request:', error);
      alert(error.response?.data?.error || 'Failed to launch request');
    }
  };

  const handleReopenRequest = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Reopen this closed request?')) {
      return;
    }

    try {
      await fileRequestApi.reopenRequest(id);
      fetchRequests();
    } catch (error: any) {
      console.error('Failed to reopen request:', error);
      alert(error.response?.data?.error || 'Failed to reopen request');
    }
  };


  // Duplicate with canvas brief
  const handleDuplicate = async (request: any) => {
    const data: any = {
      requestType: request.request_type,
      platforms: request.platforms || (request.platform ? [request.platform] : []),
      verticals: request.verticals || (request.vertical ? [request.vertical] : []),
      conceptNotes: request.concept_notes || '',
      numCreatives: request.num_creatives ? String(request.num_creatives) : '',
      sourceRequestId: request.id,
      assignedBuyerIds: request.assigned_buyer_ids || (request.assigned_buyer_id ? [request.assigned_buyer_id] : []),
      allowMultipleUploads: request.allow_multiple_uploads,
      requireEmail: request.require_email,
      customMessage: request.custom_message || '',
    };

    // Fetch canvas brief if it exists (including attachments)
    try {
      const canvasRes = await fileRequestApi.canvas.get(request.id);
      const canvas = canvasRes.data?.canvas || canvasRes.data?.data;
      if (canvas?.content) {
        data.canvasHeadline = canvas.content.headline || '';
        data.canvasScript = canvas.content.script || '';
      }
      // Include attachments for duplication
      if (canvas?.attachments && canvas.attachments.length > 0) {
        data.canvasAttachments = canvas.attachments;
      }
    } catch (_) {
      // No canvas or error — continue without it
    }

    setDuplicateData(data);
    setShowCreateModal(true);
  };

  // Bulk action handlers
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredRequests.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredRequests.map(r => r.id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkClose = async () => {
    if (selectedIds.size === 0) return;
    const activeSelected = filteredRequests.filter(r => selectedIds.has(r.id) && r.is_active);
    if (activeSelected.length === 0) {
      alert('No active requests selected to close.');
      return;
    }
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Close ${activeSelected.length} request(s)? They will no longer accept uploads.`)) return;
    setBulkActionLoading(true);
    let success = 0, failed = 0;
    for (const req of activeSelected) {
      try {
        await fileRequestApi.close(req.id);
        success++;
      } catch { failed++; }
    }
    setBulkActionLoading(false);
    setSelectedIds(new Set());
    fetchRequests();
    if (failed > 0) alert(`Closed ${success}, failed ${failed}`);
  };

  const handleBulkReopen = async () => {
    if (selectedIds.size === 0) return;
    const closedSelected = filteredRequests.filter(r => selectedIds.has(r.id) && (r.status === 'closed' || !r.is_active));
    if (closedSelected.length === 0) {
      alert('No closed requests selected to reopen.');
      return;
    }
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Reopen ${closedSelected.length} request(s)?`)) return;
    setBulkActionLoading(true);
    let success = 0, failed = 0;
    for (const req of closedSelected) {
      try {
        await fileRequestApi.reopenRequest(req.id);
        success++;
      } catch { failed++; }
    }
    setBulkActionLoading(false);
    setSelectedIds(new Set());
    fetchRequests();
    if (failed > 0) alert(`Reopened ${success}, failed ${failed}`);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Delete ${selectedIds.size} request(s)? This action cannot be undone.`)) return;
    setBulkActionLoading(true);
    let success = 0, failed = 0;
    for (const id of Array.from(selectedIds)) {
      try {
        await fileRequestApi.delete(id);
        success++;
      } catch { failed++; }
    }
    setBulkActionLoading(false);
    setSelectedIds(new Set());
    fetchRequests();
    if (failed > 0) alert(`Deleted ${success}, failed ${failed}`);
  };

  // Filtering is now server-side; requests are already filtered
  const filteredRequests = requests;

  return (
    <DashboardLayout
      title="File Requests"
      actions={
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex gap-1 border rounded-lg p-1">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
          {/* Only show Create Request button for non-creative users */}
          {user?.role !== 'creative' && (
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Request
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-6">
        {/* Status Filters */}
        <div className="flex gap-2">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
          >
            All
          </Button>
          <Button
            variant={filter === 'active' ? 'default' : 'outline'}
            onClick={() => setFilter('active')}
          >
            Active
          </Button>
          <Button
            variant={filter === 'closed' ? 'default' : 'outline'}
            onClick={() => setFilter('closed')}
          >
            Closed
          </Button>
        </div>

        {/* Vertical Filters */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Vertical:</span>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedVertical === 'All' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedVertical('All')}
              >
                All
              </Button>
              {verticals.map((vertical) => (
                <Button
                  key={vertical}
                  variant={selectedVertical === vertical ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedVertical(vertical)}
                >
                  {vertical}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Advanced Filters */}
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4" />
              <span className="font-medium">Filters</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            >
              {showAdvancedFilters ? 'Hide' : 'Show'} Advanced
            </Button>
          </div>

          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by title, description, or buyer name..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* Date Filters + Sort — always visible */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Date From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Date To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Sort By</label>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="created_at">Created Date</option>
                  <option value="deadline">Deadline</option>
                  <option value="updated_at">Last Updated</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Order</label>
                <select value={sortOrder} onChange={e => setSortOrder(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>
            </div>

            {/* Advanced Filters */}
            {showAdvancedFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Platform Filter */}
                <div>
                  <label className="block text-sm font-medium mb-2">Platform</label>
                  <SearchableSelect
                    value={selectedPlatform}
                    onChange={setSelectedPlatform}
                    placeholder="All Platforms"
                    options={[
                      { value: 'All', label: 'All Platforms' },
                      ...PLATFORMS.map(p => ({ value: p, label: p }))
                    ]}
                  />
                </div>

                {/* Vertical Filter (Dropdown) */}
                <div>
                  <label className="block text-sm font-medium mb-2">Vertical</label>
                  <SearchableSelect
                    value={selectedVertical}
                    onChange={setSelectedVertical}
                    placeholder="All Verticals"
                    options={[
                      { value: 'All', label: 'All Verticals' },
                      ...[...verticals].map(v => ({ value: v, label: v }))
                    ]}
                  />
                </div>

                {/* Editor Filter */}
                <div>
                  <label className="block text-sm font-medium mb-2">Editors</label>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant={selectedEditorIds.length === 0 ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedEditorIds([])}
                    >
                      All Editors
                    </Button>
                    {editors.map((editor) => (
                      <Button
                        key={editor.id}
                        variant={selectedEditorIds.includes(editor.id) ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          if (selectedEditorIds.includes(editor.id)) {
                            setSelectedEditorIds(selectedEditorIds.filter(id => id !== editor.id));
                          } else {
                            setSelectedEditorIds([...selectedEditorIds, editor.id]);
                          }
                        }}
                      >
                        {editor.display_name || editor.name}
                      </Button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Click to toggle editor selection</p>
                </div>

                {/* Media Type Filter */}
                <div>
                  <label className="block text-sm font-medium mb-2">Media Type</label>
                  <div className="flex gap-2">
                    <Button
                      variant={selectedMediaType === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedMediaType('all')}
                    >
                      All
                    </Button>
                    <Button
                      variant={selectedMediaType === 'image' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedMediaType('image')}
                    >
                      Images
                    </Button>
                    <Button
                      variant={selectedMediaType === 'video' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedMediaType('video')}
                    >
                      Videos
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Bulk Action Bar */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {selectedIds.size} selected
            </span>
            <div className="flex items-center gap-2 ml-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkClose}
                disabled={bulkActionLoading}
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
              >
                <XCircle className="w-3.5 h-3.5 mr-1" />
                Close
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkReopen}
                disabled={bulkActionLoading}
                className="text-green-600 border-green-300 hover:bg-green-50"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1" />
                Reopen
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkDelete}
                disabled={bulkActionLoading}
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Delete
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedIds(new Set())}
                disabled={bulkActionLoading}
              >
                Clear
              </Button>
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : filteredRequests.length > 0 ? (
          viewMode === 'table' ? (
            /* Table View */
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="p-4 w-10">
                      <input
                        type="checkbox"
                        checked={filteredRequests.length > 0 && selectedIds.size === filteredRequests.length}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th className="text-left p-4 font-medium">Date</th>
                    <th className="text-left p-4 font-medium">Requested By</th>
                    <th className="text-left p-4 font-medium">Platform</th>
                    <th className="text-left p-4 font-medium">Creatives</th>
                    <th className="text-left p-4 font-medium">Type</th>
                    <th className="text-left p-4 font-medium">Concept Notes</th>
                    <th className="text-left p-4 font-medium">Request To</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.map((request) => {
                    // Calculate progress for row color (always use global progress)
                    const total = request.num_creatives || 0;
                    const uploaded = request.upload_count ?? 0;
                    const pct = (total && total > 0) ? Math.min(100, Math.round((uploaded / total) * 100)) : 0;
                    const status = request.status || (request.is_active ? 'open' : 'closed');

                    // Determine row background color (using lightest shades)
                    let rowBgClass = '';
                    if (status === 'closed' || !request.is_active) {
                      rowBgClass = 'bg-gray-50 dark:bg-gray-800/30';
                    } else if (pct === 100) {
                      rowBgClass = 'bg-green-50 dark:bg-green-900/10';
                    } else if (pct > 0) {
                      rowBgClass = 'bg-yellow-50 dark:bg-yellow-900/10';
                    } else {
                      rowBgClass = 'bg-red-50 dark:bg-red-900/10';
                    }

                    return (
                    <tr key={request.id} className={`border-b hover:bg-opacity-70 hover:brightness-95 ${rowBgClass}`}>
                      <td className="p-4 w-10">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(request.id)}
                          onChange={() => toggleSelect(request.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="p-4 text-sm">{formatDateTime(request.created_at)}</td>
                      <td className="p-4 text-sm">
                        {request.created_by_name || '-'}
                      </td>
                      <td className="p-4">
                        {(request.platforms && request.platforms.length > 0) || request.platform ? (
                          <div className="flex flex-wrap gap-1">
                            {(request.platforms || (request.platform ? [request.platform] : [])).map((platform, idx) => (
                              <span key={idx} className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                {platform}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-4 text-sm">
                        {(() => {
                          const globalTotal = request.num_creatives || 0;
                          const globalUploaded = request.upload_count ?? 0;
                          const myTotal = user?.role === 'creative'
                            ? (request.my_creatives_assigned && request.my_creatives_assigned > 0
                                ? request.my_creatives_assigned
                                : globalTotal)
                            : globalTotal;
                          const myUploaded = user?.role === 'creative'
                            ? Number(request.my_uploaded_files_count ?? 0)
                            : globalUploaded;
                          if (!globalTotal) return <span className="text-muted-foreground">-</span>;
                          const globalPct = Math.min(100, Math.round((globalUploaded / globalTotal) * 100));
                          const barColor = globalPct >= 100 ? 'bg-green-500' : globalPct >= 60 ? 'bg-yellow-400' : 'bg-blue-500';
                          return (
                            <div className="min-w-[80px]" title={`Total: ${globalUploaded}/${globalTotal} uploaded`}>
                              <div className="flex items-center justify-between text-xs mb-0.5">
                                <span>{globalUploaded}/{globalTotal}</span>
                                <span className="font-medium">{globalPct}%</span>
                              </div>
                              <div className="w-full bg-muted rounded-full h-1.5">
                                <div
                                  className={`${barColor} h-1.5 rounded-full transition-all`}
                                  style={{ width: `${globalPct}%` }}
                                />
                              </div>
                              {user?.role === 'creative' && myTotal !== globalTotal && (
                                <div className="text-xs text-gray-500 mt-0.5">
                                  You: {myUploaded}/{myTotal}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="p-4">
                        {(request.verticals && request.verticals.length > 0) || request.vertical ? (
                          <div className="flex flex-wrap gap-1">
                            {(request.verticals || (request.vertical ? [request.vertical] : [])).map((vertical, idx) => (
                              <span key={idx} className={getVerticalBadgeClasses(vertical)}>
                                {vertical}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-4 text-sm max-w-xs truncate" title={request.concept_notes || request.canvas_headline || ''}>
                        {request.concept_notes || request.canvas_headline ? (
                          <span>
                            {request.concept_notes || request.canvas_headline}
                            {!request.concept_notes && request.canvas_headline && (
                              <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">Brief</span>
                            )}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="p-4 text-sm">
                        {request.assigned_editors || '-'}
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col gap-1 items-start">
                          <span className={getStatusBadgeClasses(request.status || (request.is_active ? 'open' : 'closed'))}>
                            {(request.status || (request.is_active ? 'open' : 'closed')).replace('_', ' ')}
                          </span>
                          {/* Per-editor deadline indicator (only meaningful for assigned creatives) */}
                          {(request.my_deadline_at || request.my_delivered_at) && (
                            <DeadlineChip
                              deadlineAt={request.my_deadline_at}
                              deliveredAt={request.my_delivered_at}
                              deadlineMissed={request.my_deadline_missed}
                            />
                          )}
                          {/* Ad-verified launch summary (filename found in a spending external ad) */}
                          {((request.launched_files_count ?? 0) > 0 || (request.not_launched_files_count ?? 0) > 0) && (
                            <div className="flex flex-wrap gap-1 mt-0.5" title="Verified against the ads dashboard: did each delivered creative actually run as a spending ad">
                              {(request.launched_files_count ?? 0) > 0 && (
                                <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                                  ✓ {request.launched_files_count} launched
                                </span>
                              )}
                              {(request.not_launched_files_count ?? 0) > 0 && (
                                <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
                                  ✗ {request.not_launched_files_count} not launched
                                </span>
                              )}
                            </div>
                          )}
                          {/* Testing summary badge */}
                          {((request.tested_files_count ?? 0) > 0 || (request.untested_files_count ?? 0) > 0 || (request.scheduled_files_count ?? 0) > 0) && (
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {(request.tested_files_count ?? 0) > 0 && (
                                <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                                  ✓ {request.tested_files_count} launched
                                </span>
                              )}
                              {(request.scheduled_files_count ?? 0) > 0 && (
                                <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300">
                                  ⏳ {request.scheduled_files_count} scheduled
                                </span>
                              )}
                              {(request.untested_files_count ?? 0) > 0 && (
                                <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
                                  ✗ {request.untested_files_count} not launched
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {/* 1. View Details */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(request)}
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {/* 2. Close */}
                          {request.is_active && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCloseRequest(request.id)}
                              title="Close"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          )}
                          {/* 3. Launch */}
                          {(user?.id === request.created_by || user?.id === request.assigned_buyer_id || (request as any).assigned_buyer_ids?.includes(user?.id)) &&
                           request.status === 'uploaded' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-green-600 hover:text-green-700"
                              onClick={(e) => handleLaunchRequest(request.id, e)}
                              title="Mark as Launched"
                            >
                              <Rocket className="w-4 h-4" />
                            </Button>
                          )}
                          {/* 4. Reopen */}
                          {(user?.id === request.created_by || user?.id === request.assigned_buyer_id || (request as any).assigned_buyer_ids?.includes(user?.id)) &&
                           request.status === 'closed' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-orange-600 hover:text-orange-700"
                              onClick={(e) => handleReopenRequest(request.id, e)}
                              title="Reopen Request"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Generate with ORBIT — admin-only. A real, legible button, not a
                              tiny glyph nobody can find. */}
                          {canUseStudio(user) && (
                            <button
                              onClick={() => setOrbitFor(request)}
                              title="Auto-fulfil this request with ORBIT"
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium
                                         bg-violet-600 text-white hover:bg-violet-700 shadow-sm whitespace-nowrap"
                            >
                              <Sparkles className="w-3.5 h-3.5" /> ORBIT
                            </button>
                          )}
                          {/* 5. Copy Link */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopyLink(request.request_token)}
                            title="Copy Link"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          {/* 6. Delete */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteRequest(request.id)}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          {/* 7. Duplicate */}
                          {(user?.role === 'buyer' || isAdminRole(user?.role)) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDuplicate(request);
                              }}
                              title="Duplicate Request"
                            >
                              <Copy className="w-4 h-4 text-purple-500" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            /* Grid View */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredRequests.map((request) => {
                // Calculate progress for card color
                const total = request.num_creatives;
                const uploaded = request.upload_count ?? 0;
                const pct = (total && total > 0) ? Math.min(100, Math.round((uploaded / total) * 100)) : 0;
                const status = request.status || (request.is_active ? 'open' : 'closed');

                // Determine card background color (using lightest shades)
                let cardBgClass = '';
                if (status === 'closed' || !request.is_active) {
                  cardBgClass = 'bg-gray-50 dark:bg-gray-800/30';
                } else if (pct === 100) {
                  cardBgClass = 'bg-green-50 dark:bg-green-900/10';
                } else if (pct > 0) {
                  cardBgClass = 'bg-yellow-50 dark:bg-yellow-900/10';
                } else {
                  cardBgClass = 'bg-red-50 dark:bg-red-900/10';
                }

                return (
                <Card key={request.id} className={`p-6 ${cardBgClass} ${selectedIds.has(request.id) ? 'ring-2 ring-blue-500' : ''}`}>
                  <div className="space-y-4">
                    {/* Status Badge + Checkbox */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(request.id)}
                          onChange={() => toggleSelect(request.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className={getStatusBadgeClasses(request.status || (request.is_active ? 'open' : 'closed'))}>
                          {(request.status || (request.is_active ? 'open' : 'closed')).replace('_', ' ')}
                        </span>
                      </div>
                      {/* Creatives progress */}
                      {(() => {
                        const total = request.num_creatives;
                        const uploaded = request.upload_count ?? 0;
                        if (!total) return (
                          <span className="text-sm text-muted-foreground">
                            {uploaded} {uploaded === 1 ? 'file' : 'files'}
                          </span>
                        );
                        const pct = Math.min(100, Math.round((uploaded / total) * 100));
                        const barColor = pct >= 100 ? 'bg-green-500' : pct >= 60 ? 'bg-yellow-400' : 'bg-blue-500';
                        return (
                          <div className="w-24" title={`${uploaded} / ${total} uploaded`}>
                            <div className="flex items-center justify-between text-xs mb-0.5">
                              <span className="text-muted-foreground">{uploaded}/{total}</span>
                              <span className="font-medium">{pct}%</span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-1.5">
                              <div className={`${barColor} h-1.5 rounded-full transition-all`} style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Title & Description */}
                    <div>
                      <h3 className="font-semibold text-lg">{request.title}</h3>
                      {request.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                          {request.description}
                        </p>
                      )}
                    </div>

                    {/* Meta Info */}
                    <div className="space-y-1 text-sm text-muted-foreground">
                      {request.buyer_name && (
                        <p>Buyer: {request.buyer_name}</p>
                      )}
                      {((request.platforms && request.platforms.length > 0) || request.platform) && (
                        <div className="flex items-center gap-2">
                          <span>Platform{((request.platforms?.length || 0) > 1) ? 's' : ''}:</span>
                          <div className="flex flex-wrap gap-1">
                            {(request.platforms || (request.platform ? [request.platform] : [])).map((platform, idx) => (
                              <span key={idx} className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                {platform}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {((request.verticals && request.verticals.length > 0) || request.vertical) && (
                        <div className="flex items-center gap-2">
                          <span>Vertical{((request.verticals?.length || 0) > 1) ? 's' : ''}:</span>
                          <div className="flex flex-wrap gap-1">
                            {(request.verticals || (request.vertical ? [request.vertical] : [])).map((vertical, idx) => (
                              <span key={idx} className={getVerticalBadgeClasses(vertical)}>
                                {vertical}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {request.num_creatives && (
                        <p>Creatives: {request.num_creatives}</p>
                      )}
                      {(request.concept_notes || request.canvas_headline) && (
                        <p className="line-clamp-2">
                          {request.concept_notes ? (
                            <>Notes: {request.concept_notes}</>
                          ) : (
                            <>Brief: {request.canvas_headline} <span className="text-[10px] px-1 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">Canvas</span></>
                          )}
                        </p>
                      )}
                      {request.assigned_editors && (
                        <p>Editors: {request.assigned_editors}</p>
                      )}
                      {/* Testing summary */}
                      {((request.tested_files_count ?? 0) > 0 || (request.untested_files_count ?? 0) > 0 || (request.scheduled_files_count ?? 0) > 0) && (
                        <div className="flex flex-wrap gap-1">
                          {(request.tested_files_count ?? 0) > 0 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                              ✓ {request.tested_files_count} launched
                            </span>
                          )}
                          {(request.scheduled_files_count ?? 0) > 0 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300">
                              ⏳ {request.scheduled_files_count} scheduled
                            </span>
                          )}
                          {(request.untested_files_count ?? 0) > 0 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
                              ✗ {request.untested_files_count} not launched
                            </span>
                          )}
                        </div>
                      )}
                      {request.folder_name && (
                        <p>Folder: {request.folder_name}</p>
                      )}
                      {request.deadline && (
                        <p>Deadline: {formatDate(request.deadline)}</p>
                      )}
                      <p>Created: {formatDateTime(request.created_at)}</p>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyLink(request.request_token)}
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copy Link
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetails(request)}
                      >
                        <LinkIcon className="w-3 h-3 mr-1" />
                        Details
                      </Button>
                      {(isAdminRole(user?.role) || (user?.role as string) === 'vertical_head' || (user?.role as string) === 'assistant_team_lead' || ((user as any)?.additional_roles || []).some((r: string) => ['vertical_head', 'assistant_team_lead', 'team_lead'].includes(r))) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleReassign(request)}
                        >
                          <UserPlus className="w-3 h-3 mr-1" />
                          Reassign
                        </Button>
                      )}
                      {/* Launch button - creator/buyer when status is uploaded */}
                      {(user?.id === request.created_by || user?.id === request.assigned_buyer_id || (request as any).assigned_buyer_ids?.includes(user?.id)) &&
                       request.status === 'uploaded' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-green-600 hover:text-green-700"
                          onClick={(e) => handleLaunchRequest(request.id, e)}
                        >
                          <Rocket className="w-3 h-3 mr-1" />
                          Launch
                        </Button>
                      )}
                      {/* Reopen button - creator/buyer when status is closed */}
                      {(user?.id === request.created_by || user?.id === request.assigned_buyer_id || (request as any).assigned_buyer_ids?.includes(user?.id)) &&
                       request.status === 'closed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-orange-600 hover:text-orange-700"
                          onClick={(e) => handleReopenRequest(request.id, e)}
                        >
                          <RotateCcw className="w-3 h-3 mr-1" />
                          Reopen
                        </Button>
                      )}
                      {request.is_active && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCloseRequest(request.id)}
                        >
                          <XCircle className="w-3 h-3 mr-1" />
                          Close
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteRequest(request.id)}
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Delete
                      </Button>
                      {(user?.role === 'buyer' || isAdminRole(user?.role)) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDuplicate(request)}
                        >
                          <Copy className="w-3 h-3 mr-1 text-purple-500" />
                          Duplicate
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
                );
              })}
            </div>
          )
        ) : fetchError ? (
          <Card className="p-12 border-red-300 dark:border-red-700">
            <div className="text-center">
              <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
              <h3 className="text-lg font-semibold mb-2 text-red-700 dark:text-red-300">Couldn't load file requests</h3>
              <p className="text-muted-foreground mb-4">{fetchError}</p>
              <Button onClick={() => fetchRequests()}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Try again
              </Button>
            </div>
          </Card>
        ) : (
          <Card className="p-12">
            <div className="text-center">
              <Inbox className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No file requests found</h3>
              <p className="text-muted-foreground mb-4">
                {totalRequests === 0
                  ? 'Create a file request to start collecting files from external parties'
                  : 'Try adjusting your filters to see more results'}
              </p>
              {totalRequests === 0 && (user?.role === 'buyer' || isAdminRole(user?.role)) && (
                <Button onClick={() => setShowCreateModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Request
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t pt-4">
            <p className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * PAGE_SIZE) + 1}-{Math.min(currentPage * PAGE_SIZE, totalRequests)} of {totalRequests} requests
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={pageNum === currentPage ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className="min-w-[36px]"
                  >
                    {pageNum}
                  </Button>
                );
              })}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateFileRequestModal
          onClose={() => { setShowCreateModal(false); setDuplicateData(null); }}
          onSuccess={() => {
            setShowCreateModal(false);
            setDuplicateData(null);
            fetchRequests();
          }}
          initialData={duplicateData || undefined}
        />
      )}

      {showDetailsModal && selectedRequest && (
        <FileRequestDetailsModal
          requestId={selectedRequest.id}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedRequest(null);
          }}
          onUpdate={() => {
            fetchRequests();
          }}
        />
      )}

      {showReassignModal && requestToReassign && (
        <ReassignFileRequestModal
          requestId={requestToReassign.id}
          requestTitle={requestToReassign.title}
          currentEditors={
            requestToReassign.assigned_editors
              ? requestToReassign.assigned_editors.split(', ').map((name, idx) => ({ id: `editor-${idx}`, name }))
              : []
          }
          numCreatives={requestToReassign.num_creatives || 0}
          onClose={() => {
            setShowReassignModal(false);
            setRequestToReassign(null);
          }}
          onSuccess={() => {
            setShowReassignModal(false);
            setRequestToReassign(null);
            fetchRequests();
          }}
        />
      )}

      {/* ORBIT — shows the work order it parsed from the request, then builds it. On start we
          take the user straight to the Creative Team Room so they can watch it being made. */}
      {orbitFor && (
        <OrbitModal
          requestId={orbitFor.id}
          requestTitle={orbitFor.title || orbitFor.request_type}
          onClose={() => setOrbitFor(null)}
          onStarted={() => {
            setOrbitFor(null);
            fetchRequests();
            navigate('/variation-studio?tab=team');
          }}
        />
      )}
    </DashboardLayout>
  );
}
